import { useMemo } from 'react';
import { useTheme as muiUseTheme } from '@mui/material/styles';
import { type Theme as muiTheme } from '@mui/material/styles';
import { getLightThemeOptions, getLightTheme } from './themes/light';
import { getDarkTheme } from './themes/dark';
import { ThemeNames } from './themeProvider';
import { useBrandingOverrides } from './brandingContext';
import { themeDeepMerge } from './utils/themeDeepMerge';
import { paletteTokens } from './generated/paletteVariables';

export type LightThemeOptions = ReturnType<typeof getLightThemeOptions>;

type ThemePalette = muiTheme['palette'] &
  Omit<LightThemeOptions['palette'], 'mode'> & { mode: 'light' | 'dark' };

export type Theme = Omit<muiTheme & LightThemeOptions, 'palette'> & {
  palette: ThemePalette;
};

const themeGetters = {
  light: getLightTheme,
  dark: getDarkTheme,
};

/**
 * Hook to access the current theme or a specific theme variant.
 *
 * @param themeName - Optional. If provided, returns the branded version of that
 *   theme variant. If omitted, returns the current theme from MUI context.
 *
 * When a themeName is specified, the hook will:
 * 1. Get branding overrides from BrandingContext (if available)
 * 2. Apply those overrides to the requested theme variant
 * 3. Return the branded theme
 */
export function useTheme(themeName?: ThemeNames): Theme {
  const contextTheme = muiUseTheme<Theme>();
  const branding = useBrandingOverrides();

  const computedTheme = useMemo(() => {
    if (themeName === undefined) {
      return contextTheme;
    }

    const overridePalette = branding?.overridePalette;
    const overrideTheme = branding?.overrideTheme;

    const palette = overridePalette
      ? (themeDeepMerge(paletteTokens, overridePalette) as typeof paletteTokens)
      : undefined;

    const baseTheme = themeGetters[themeName](palette);

    if (!overrideTheme) {
      return baseTheme as Theme;
    }

    return themeDeepMerge(
      baseTheme as unknown as Record<string, unknown>,
      overrideTheme,
    ) as Theme;
  }, [themeName, contextTheme, branding]);

  return computedTheme;
}
